import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetSalesforceLeadFieldsQuery,
	useGetSalesforceOwnersQuery,
} from "@/pages/Private/redux/salesforce/salesforce.api";
import {
	useGetSalesforceLeadConfigQuery,
	useSaveSalesforceLeadConfigMutation,
} from "@/pages/Private/redux/salesforce/salesforceDeal.api";

import { UpdateSalesforceLeadConfig } from "../schema/salesforceDeal";

interface LeadConfigDraft {
	leadTopicTemplate: string;
	defaultOwnerId: string | null;
	estimatedValue: number | null;
	defaultLeadStatus: string | null;
}

const EMPTY_DRAFT: LeadConfigDraft = {
	leadTopicTemplate: "",
	defaultOwnerId: null,
	estimatedValue: null,
	defaultLeadStatus: null,
};

/** `PUT lead-config` patches — an unset value is omitted, never sent as `null`. */
const buildPayload = (draft: LeadConfigDraft): UpdateSalesforceLeadConfig => {
	const payload: UpdateSalesforceLeadConfig = {};

	if (draft.leadTopicTemplate.trim()) {
		payload.leadTopicTemplate = draft.leadTopicTemplate.trim();
	}

	if (draft.defaultOwnerId) {
		payload.defaultOwnerId = draft.defaultOwnerId;
	}

	if (draft.estimatedValue !== null) {
		payload.estimatedValue = draft.estimatedValue;
	}

	if (draft.defaultLeadStatus) {
		payload.defaultLeadStatus = draft.defaultLeadStatus;
	}

	return payload;
};

/**
 * Lead defaults.
 *
 * Two things differ from the Dynamics tab, both because Salesforce's `Lead` differs:
 *
 * - **`estimatedValue` is stored but never written.** A standard Lead has no currency field at
 *   all (§4.4's ticket-6 correction), so the column round-trips for the customer's reference
 *   only. The help text says so rather than leaving it as a silent no-op — a number that looks
 *   saved and never reaches the CRM is worse than an absent field.
 * - **`defaultLeadStatus` is real, and its options come from the org's own `Status` picklist**
 *   rather than a hard-coded list. It is written as a *default*: an active LEAD mapping onto
 *   `Status` always wins, because there is no lead equivalent of the Opportunity reserved-field
 *   list.
 *
 * There is no close-date offset: a Salesforce Lead has no close date to offset.
 */
export const SalesforceLeadConfigTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.salesforce.leadConfig.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: leadConfig, isLoading: isLoadingConfig } = useGetSalesforceLeadConfigQuery();
	const { data: ownersData } = useGetSalesforceOwnersQuery();
	const { data: leadFields } = useGetSalesforceLeadFieldsQuery();
	const [saveLeadConfig, { isLoading: isSaving }] = useSaveSalesforceLeadConfigMutation();

	const [draft, setDraft] = useState<LeadConfigDraft>(EMPTY_DRAFT);

	useEffect(() => {
		if (!leadConfig) {
			return;
		}

		setDraft({
			leadTopicTemplate: leadConfig.leadTopicTemplate ?? "",
			defaultOwnerId: leadConfig.defaultOwnerId ?? null,
			estimatedValue: leadConfig.estimatedValue ?? null,
			defaultLeadStatus: leadConfig.defaultLeadStatus ?? null,
		});
	}, [leadConfig]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			(ownersData?.owners ?? []).map((owner) => ({
				id: owner.id,
				title: owner.name ? `${owner.name} (${owner.email})` : owner.email,
			})),
		[ownersData]
	);

	// Straight off the org's `Lead.Status` describe — an org can rename or restrict these, so a
	// hard-coded list would offer values the customer's own picklist rejects.
	const statusOptions: DropdownAutoCompleteItem[] = useMemo(() => {
		const statusField = (leadFields?.fields ?? []).find((field) => field.name === "Status");

		return (statusField?.options ?? []).map((option) => ({
			id: option.value,
			title: option.label,
		}));
	}, [leadFields]);

	const handleSave = async () => {
		try {
			await saveLeadConfig(buildPayload(draft)).unwrap();
			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		}
	};

	if (isLoadingConfig) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex w-full flex-col gap-4">
			{renderMessageSnackbar()}
			<div className="mb-1 text-sm text-text-meta">{ts("description")}</div>

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div className="md:col-span-2">
					<InputField
						handleChange={(event) =>
							setDraft((prev) => ({ ...prev, leadTopicTemplate: event.target.value }))
						}
						information={ts("leadTopicTemplateHelp")}
						label={ts("leadTopicTemplate")}
						name="leadTopicTemplate"
						placeholder={ts("leadTopicTemplatePlaceholder")}
						value={draft.leadTopicTemplate}
					/>
				</div>

				<Dropdown
					data={ownerOptions}
					defaultValue={ownerOptions.find((option) => option.id === draft.defaultOwnerId)}
					floating={true}
					handleSelect={(value) =>
						setDraft((prev) => ({ ...prev, defaultOwnerId: value.id as string }))
					}
					label={ts("defaultOwner")}
				/>

				{statusOptions.length > 0 && (
					<div data-testid="salesforce-lead-status">
						<Dropdown
							data={statusOptions}
							defaultValue={statusOptions.find((option) => option.id === draft.defaultLeadStatus)}
							floating={true}
							handleSelect={(value) =>
								setDraft((prev) => ({ ...prev, defaultLeadStatus: value.id as string }))
							}
							information={ts("defaultLeadStatusHelp")}
							label={ts("defaultLeadStatus")}
						/>
					</div>
				)}

				<InputField
					handleChange={(event) => {
						const parsed = parseFloat(event.target.value);

						setDraft((prev) => ({
							...prev,
							estimatedValue: Number.isFinite(parsed) && parsed >= 0 ? parsed : null,
						}));
					}}
					information={ts("estimatedValueHelp")}
					label={ts("estimatedValue")}
					min={0}
					name="estimatedValue"
					testId="salesforce-lead-estimated-value"
					type="number"
					value={draft.estimatedValue ?? ""}
				/>
			</div>

			<div className="mt-4 flex justify-end">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					testId="salesforce-lead-config-save"
					title={t("basics.save")}
					type="button"
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
